import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

const FileIntersectionObserverContext =
  createContext<null | IntersectionObserver>(null);

export const useFileIntersectionObserver = () => {
  return useContext(FileIntersectionObserverContext);
};

export const FileIntersectionObserverProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [observer, setObserver] = useState<null | IntersectionObserver>(null);

  useEffect(() => {
    const observerInstance = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const lazyFile = entry.target as
              | HTMLImageElement
              | HTMLVideoElement;

            const loadFile = () => {
              const datasetSrc = lazyFile.dataset.src;
              if (datasetSrc) {
                lazyFile.src = datasetSrc;
              }
            };

            if ('requestIdleCallback' in window) {
              window.requestIdleCallback(loadFile);
            } else {
              setTimeout(loadFile, 100);
            }

            observerInstance.unobserve(lazyFile);
          }
        });
      },
      { rootMargin: '0px', threshold: 0.1 },
    );

    setObserver(observerInstance);

    return () => observerInstance.disconnect();
  }, []);

  return (
    <FileIntersectionObserverContext.Provider value={observer}>
      {children}
    </FileIntersectionObserverContext.Provider>
  );
};
